import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { api } from '@api/apiCompat';
import type { AppDispatch } from '@store/store';
import { tokenStorage } from '@utils/tokenStorage';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Structured contact from backend
interface AdditionalContact {
  name?: string;
  email?: string;
  phone?: string;
  title?: string;
}

interface UnlockResult {
  success: boolean;
  unlocked_count?: number;
  total_cost?: number;
  new_balance?: number;
  leads?: Record<
    string,
    {
      additional_contacts?: AdditionalContact[];
      additional_emails?: string[];
      additional_phones?: string[];
    }
  >;
  error?: string;
}

interface PricingInfo {
  price_per_lead: number;
  description: string;
}

export const useUnlockAdditionalContacts = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  // Get the auth token from tokenStorage
  const getAuthToken = (): string | null => tokenStorage.getAccessToken();

  // Refresh the balance in the UI
  const refreshBalance = useCallback(() => {
    // Invalidate the balance cache to trigger a refetch
    // This will cause the AddTokensButton to update with the new balance
    dispatch(api.util.invalidateTags(['Balance'] as any));

    // Also try to refetch directly
    dispatch(api.endpoints.billingBalanceRetrieve.initiate(undefined, { forceRefetch: true }) as any);
  }, [dispatch]);

  // Unlock additional contacts for one or more leads
  const unlockContacts = useCallback(
    async (leadIds: string | string[]): Promise<UnlockResult> => {
      console.log('[useUnlockAdditionalContacts] unlockContacts called with:', leadIds);

      const token = getAuthToken();
      console.log('[useUnlockAdditionalContacts] Auth token:', token ? 'present' : 'missing');
      if (!token) {
        console.log('[useUnlockAdditionalContacts] Not authenticated');
        return { success: false, error: 'Not authenticated' };
      }

      const ids = Array.isArray(leadIds) ? leadIds : [leadIds];
      setLoading(true);
      setError(null);

      try {
        const url = `${API_BASE_URL}/scrapeking-database/unlock-contacts/`;
        console.log('[useUnlockAdditionalContacts] Fetching:', url);
        console.log('[useUnlockAdditionalContacts] Payload:', { lead_ids: ids });

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ lead_ids: ids }),
        });

        console.log('[useUnlockAdditionalContacts] Response status:', response.status);
        const data = await response.json();
        console.log('[useUnlockAdditionalContacts] Response data:', data);

        if (!response.ok) {
          const errorMsg = data.error || `HTTP ${response.status}`;
          setError(errorMsg);
          return { success: false, error: errorMsg };
        }

        // Refresh balance to trigger the animation in the top bar
        refreshBalance();

        return {
          success: true,
          unlocked_count: data.unlocked_count,
          total_cost: data.total_cost,
          new_balance: data.new_balance,
          leads: data.leads,
        };
      } catch (err) {
        console.error('[useUnlockAdditionalContacts] Error:', err);
        const errorMsg = err instanceof Error ? err.message : 'Failed to unlock contacts';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setLoading(false);
      }
    },
    [refreshBalance],
  );

  // Get pricing for additional contacts
  const getPricing = useCallback(async (): Promise<PricingInfo | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/leads/additional-pricing/`);
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }, []);

  // Helper to unlock a single lead and return the revealed contacts
  const unlockSingleLead = useCallback(
    async (
      leadId: string,
    ): Promise<{
      success: boolean;
      additional_contacts?: AdditionalContact[];
      additional_emails?: string[];
      additional_phones?: string[];
      new_balance?: number;
      total_cost?: number;
      error?: string;
    }> => {
      console.log('[useUnlockAdditionalContacts] unlockSingleLead called with leadId:', leadId);
      const result = await unlockContacts(leadId);
      console.log('[useUnlockAdditionalContacts] unlockSingleLead result:', result);

      if (result.success && result.leads && result.leads[leadId]) {
        return {
          success: true,
          additional_contacts: result.leads[leadId].additional_contacts,
          additional_emails: result.leads[leadId].additional_emails,
          additional_phones: result.leads[leadId].additional_phones,
          new_balance: result.new_balance,
          total_cost: result.total_cost,
        };
      }

      return {
        success: result.success,
        error: result.error,
      };
    },
    [unlockContacts],
  );

  return {
    unlockContacts,
    unlockSingleLead,
    getPricing,
    loading,
    error,
  };
};

export default useUnlockAdditionalContacts;
